import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { Control } from "react-hook-form";
import { useSnackbar } from "notistack";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import ListItemText from "@mui/material/ListItemText";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";
import Stepper from "@mui/material/Stepper";
import Typography from "@mui/material/Typography";
import GroupAddIcon from "@mui/icons-material/GroupAdd";

import type { UserCrew } from "../../shared/api-types/user";
import { StyledPaper } from "../Base";
import type { UploadableCourse, RoleGroup } from "../../features/bulk-certificate-upload";
import { AutocompleteField, AutocompleteMultiField } from "../FormRhf";
import { Autocomplete } from "../Autocomplete";
import { useCertificateForm, type FormInput, type SubmitPayload } from "./useCertificateForm";
import { CertificateDetailsForm } from "./CertificateDetailsForm";

// ============= Types =============

export interface BulkCertificateUploadProps {
  crewId: number;
  courses: UploadableCourse[];
  roles: RoleGroup[];
  users: UserCrew[];
  onSubmit: (values: SubmitPayload) => Promise<string | null>;
}

// ============= Component =============

const STEPS = ["courseStep", "usersStep", "uploadStep"] as const;

const BulkCertificateUpload = ({ crewId, courses, roles, users, onSubmit }: BulkCertificateUploadProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [activeStep, setActiveStep] = useState(0);
  const [maxVisitedStep, setMaxVisitedStep] = useState(0);
  const [roleFilter, setRoleFilter] = useState<number | "all">("all");

  const { form, validityHook, neverExpires, periodsResult, effectiveNeverExpires, getTooltipContent } =
    useCertificateForm({ crewId });

  const {
    control: _control,
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting, isValid },
  } = form;

  const control = _control as unknown as Control<FormInput, any, any>;
  const _setValue = setValue as any;

  const selectedCourse = watch("course") ?? null;
  const selectedUsers = watch("selectedUsers") ?? [];
  const watchedFiles = watch("files") ?? [];
  const completedDate = watch("completed") ?? "";

  // Derive user list for step 2
  const courseUsers = selectedCourse ? users.filter((u) => selectedCourse.userIds.includes(u.id)) : users;
  const courseUserIdSet = new Set(courseUsers.map((u) => u.id));
  const visibleRoles = roles.filter((r) => r.userIds.some((id) => courseUserIdSet.has(id)));
  const filteredUsers =
    roleFilter === "all"
      ? courseUsers
      : courseUsers.filter((u) => roles.find((r) => r.id === roleFilter)?.userIds.includes(u.id));

  const selectedRole = visibleRoles.find((r) => r.id === roleFilter) ?? null;
  const ADD_ROLE_SENTINEL: UserCrew = { id: -1, fullname: "__add_role__" } as unknown as UserCrew;
  const usersOptions: UserCrew[] = [ADD_ROLE_SENTINEL, ...filteredUsers];

  const handleUsersChange = (newValue: UserCrew[]) => {
    const hasSentinel = newValue.some((u) => u.id === ADD_ROLE_SENTINEL.id);
    if (!hasSentinel) return;
    const usersToAdd = selectedRole ? courseUsers.filter((u) => selectedRole.userIds.includes(u.id)) : courseUsers;
    const currentIds = new Set(selectedUsers.map((u) => u.id));
    const merged = [...selectedUsers, ...usersToAdd.filter((u) => !currentIds.has(u.id))];
    _setValue("selectedUsers", merged, { shouldValidate: true, shouldDirty: true });
  };

  const stepComplete = [!!selectedCourse, selectedUsers.length > 0, !!completedDate && watchedFiles.length > 0];

  useEffect(() => {
    setRoleFilter("all");
  }, [selectedCourse?.key]);

  const goToStep = (step: number) => {
    setActiveStep(step);
    setMaxVisitedStep((prev) => Math.max(prev, step));
  };

  const handleNext = async () => {
    if (activeStep === 1) {
      await validityHook.fetchPeriods();
    }
    goToStep(activeStep + 1);
  };

  const submit = async (values: SubmitPayload) => {
    const error = await onSubmit(values);
    if (error) {
      enqueueSnackbar(error, { variant: "error" });
    } else {
      enqueueSnackbar(t("certificate_upload.bulk.success"), { variant: "success" });
      window.history.back();
    }
  };

  const courseLabel = selectedCourse
    ? `(${selectedCourse.isCustom ? `cc_${selectedCourse.id}` : selectedCourse.id}) ${selectedCourse.name}`
    : undefined;

  return (
    <StyledPaper variant="outlined">
      <Box
        component="form"
        onSubmit={handleSubmit(submit)}
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <Stepper nonLinear alternativeLabel activeStep={activeStep}>
          {STEPS.map((step, index) => (
            <Step key={index} completed={stepComplete[index]}>
              <StepButton
                onClick={() => goToStep(index)}
                disabled={index > maxVisitedStep && !stepComplete[index - 1]}
                // MUI 9 makes the Stepper a tablist with one Tab stop on the first enabled step; keep it
                // on the current step, as before.
                tabIndex={index === activeStep ? 0 : -1}
              >
                {t(`certificate_upload.bulk.${step}`)}
              </StepButton>
            </Step>
          ))}
        </Stepper>

        {/* Step 1 — Course */}
        {activeStep === 0 && (
          <Box sx={{ maxWidth: 600 }}>
            <AutocompleteField
              control={control}
              name="course"
              options={courses}
              getOptionLabel={(o) => `(${o.isCustom ? `cc_${o.id}` : o.id}) ${o.name}`}
              isOptionEqualToValue={(a, b) => a.key === b.key}
              groupBy={(o) =>
                o.isCustom
                  ? t("certificate_upload.bulk.custom_certificates")
                  : t("certificate_upload.bulk.standard_courses")
              }
              label={`${t("certificate_upload.bulk.select_course")}*`}
            />
          </Box>
        )}

        {/* Step 2 — Users */}
        {activeStep === 1 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              maxWidth: 600,
            }}
          >
            {courseLabel && (
              <Typography variant="h4" component="h3">
                {courseLabel}
              </Typography>
            )}
            {visibleRoles.length > 0 && (
              <Autocomplete
                options={visibleRoles}
                value={visibleRoles.find((r) => r.id === roleFilter) ?? null}
                onChange={(selected) => setRoleFilter(selected?.id ?? "all")}
                getOptionLabel={(r) => r.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                label={t("certificate_upload.bulk.filter_by_role")}
              />
            )}
            <AutocompleteMultiField
              control={control}
              name="selectedUsers"
              options={usersOptions}
              getOptionLabel={(u) =>
                u.id === ADD_ROLE_SENTINEL.id
                  ? selectedRole
                    ? t("certificate_upload.bulk.add_role_users", { role: selectedRole.name })
                    : t("certificate_upload.bulk.add_all_users")
                  : u.fullname
              }
              isOptionEqualToValue={(a, b) => a.id === b.id}
              label={`${t("certificate_upload.bulk.select_users")}*`}
              minRows={4}
              disableCloseOnSelect
              onChangeEffect={handleUsersChange}
              renderOption={(props, option) => {
                if (option.id === ADD_ROLE_SENTINEL.id) {
                  const { key, ...liProps } = props as any;
                  return (
                    <li
                      key={key}
                      {...liProps}
                      style={{ ...liProps.style, borderBottom: "1px solid", borderColor: "divider" }}
                    >
                      <GroupAddIcon fontSize="small" sx={{ mr: 1, color: "primary.main" }} />
                      <ListItemText
                        primary={
                          selectedRole
                            ? t("certificate_upload.bulk.add_role_users", { role: selectedRole.name })
                            : t("certificate_upload.bulk.add_all_users")
                        }
                        slotProps={{
                          primary: { sx: { fontWeight: 600, color: "primary.main" } },
                        }}
                      />
                    </li>
                  );
                }
                const { key, ...liProps } = props as any;
                return (
                  <li key={key} {...liProps}>
                    {option.fullname}
                  </li>
                );
              }}
            />
          </Box>
        )}

        {/* Step 3 — Details */}
        {activeStep === 2 && (
          <CertificateDetailsForm
            control={control}
            neverExpires={neverExpires}
            showWarningIcon={!!periodsResult.divergentPeriods}
            getTooltipContent={() => getTooltipContent(t)}
            courseLabel={courseLabel}
            usersLabel={selectedUsers.length > 0 ? selectedUsers.map((u) => u.fullname).join(", ") : undefined}
          />
        )}

        {/* Navigation + Submit */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1,
            }}
          >
            {activeStep > 0 && (
              <Button variant="outlined" onClick={() => goToStep(activeStep - 1)}>
                {t("back")}
              </Button>
            )}
            {activeStep < 2 && (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!stepComplete[activeStep] || (activeStep === 1 && validityHook.fetching)}
                startIcon={activeStep === 1 && validityHook.fetching ? <CircularProgress size={16} /> : undefined}
              >
                {t("next")}
              </Button>
            )}
          </Box>

          <Button type="submit" variant="contained" disabled={!isValid || isSubmitting}>
            {t("certificate_upload.bulk.submit")}
          </Button>
        </Box>
      </Box>
    </StyledPaper>
  );
};

export default BulkCertificateUpload;
